import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { FaEdit, FaSearch, FaTrashAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import ScaleLoader from "react-spinners/ScaleLoader";

export default function AccountsType() {
  // States
  const [entriesList, setEntriesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  // Field States
  const [nameFieldValue, setNameFieldValue] = useState("");
  const [searchFilterText, setSearchFilterText] = useState("");

  /**
   * 1. GET LIST API Integration
   */
  const fetchAccountTypes = useCallback(async () => {
    setLoading(true);
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")) || 1609,
      api_key:
        localStorage.getItem("api_key") || "8440d41adb8335314263dc31d4504cf7",
      user_id: Number(localStorage.getItem("user_id")) || 56950,
      language: "en",
      name_search: searchFilterText,
      limit: 1000,
      page: 1,
    };

    try {
      const response = await simplePostCall(
        ApiConfig.expensesaccounttype,
        JSON.stringify(requestBody),
      );
      if (response?.result) {
        // Map API data to your component's expected format
        const formattedData = response.data.map((item) => ({
          identifier: item.expenses_id || item.expense_account_type_id,
          accountTypeName: item.expense_account_type_name,
        }));
        setEntriesList(formattedData);
      } else {
        setEntriesList([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load account types");
    } finally {
      setLoading(false);
    }
  }, [searchFilterText]);

  useEffect(() => {
    fetchAccountTypes();
  }, [fetchAccountTypes]);

  /**
   * 2. ADD & UPDATE API Integration
   */
  const handleConfirmSave = async () => {
    if (!nameFieldValue.trim()) {
      toast.warn("Please enter a name");
      return;
    }

    setLoading(true);
    const isUpdate = !!editingEntry;
    const url = isUpdate
      ? ApiConfig.AccountTypeUpdate
      : ApiConfig.AccountTypeAdd;

    const payload = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      type_name: nameFieldValue,
      ...(isUpdate && { id: editingEntry.identifier }), // Add ID only if updating
    };

    try {
      const response = await simplePostCall(url, JSON.stringify(payload));
      if (response?.result) {
        toast.success(
          isUpdate ? "Updated Successfully!" : "Created Successfully!",
        );
        setDialogOpen(false);
        fetchAccountTypes(); // Refresh list
      } else {
        toast.error(response?.message || "Operation failed");
      }
    } catch (error) {
      toast.error("Network Error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * 3. DELETE API Integration
   */
  const handlePerformDelete = async (identifier) => {
    if (!window.confirm("Are you sure you want to delete this?")) return;

    setLoading(true);
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: identifier,
    };

    try {
      const response = await simplePostCall(
        ApiConfig.AccountTypeDelete,
        JSON.stringify(requestBody),
      );
      if (response?.result) {
        toast.success("Deleted successfully");
        fetchAccountTypes();
      } else {
        toast.error(response?.message || "Failed to delete");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // UI Handlers
  const handleInitiateCreate = () => {
    setEditingEntry(null);
    setNameFieldValue("");
    setDialogOpen(true);
  };

  const handleInitiateEdit = (entry) => {
    setEditingEntry(entry);
    setNameFieldValue(entry.accountTypeName);
    setDialogOpen(true);
  };

  return (
    <div
      className="atm-outer-wrapper"
      style={{ maxWidth: "100%", margin: "auto", padding: "20px" }}
    >
      <ToastContainer />
      <div
        className="atm-top-bar"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          padding: "14px 24px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          marginBottom: "20px",
        }}
      >
        <h5
          className="atm-main-title"
          style={{
            margin: 0,
            fontWeight: "600",
            fontSize: "20px",
            color: "#333",
          }}
        >
          Account Type
        </h5>
        <div
          className="atm-button-group"
          style={{ display: "flex", gap: "12px" }}
        >
          <button
            className="atm-create-btn"
            onClick={handleInitiateCreate}
            style={{
              backgroundColor: "#53b7e8",
              border: "none",
              color: "white",
              padding: "10px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Create Account Type
          </button>
          <button
            className="atm-export-btn"
            style={{
              backgroundColor: "#53b7e8",
              border: "none",
              color: "white",
              padding: "10px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Export
          </button>
        </div>
      </div>

      <div
        className="atm-search-bar"
        style={{
          marginTop: "-18px",
          marginLeft: "-12px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "16px",
          padding: "10px 14px",
        }}
      >
        <div style={{ position: "relative", width: "230px" }}>
          <FaSearch
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#bcbbbb",
              fontSize: "13px",
            }}
          />
          <input
            type="text"
            placeholder="Search Account Type..."
            value={searchFilterText}
            onChange={(e) => setSearchFilterText(e.target.value)}
            style={{
              width: "100%",
              height: "34px",
              padding: "4px 6px 4px 30px",
              border: "1px solid #c6c9c9",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          />
        </div>
        <button
          onClick={() => setSearchFilterText("")}
          style={{
            height: "32px",
            backgroundColor: "#53b7e8",
            border: "none",
            color: "#fff",
            padding: "0 18px",
            borderRadius: "6px",
          }}
        >
          Clear
        </button>
      </div>

      <div
        className="atm-column-header-grid"
        style={{
          display: "flex",
          backgroundColor: "white",
          padding: "14px 24px",
          borderRadius: "10px",
          fontWeight: "600",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          marginBottom: "12px",
        }}
      >
        <div style={{ width: "80px", textAlign: "center" }}>Sr.No</div>
        <div style={{ flex: 1, textAlign: "center" }}>Account Type Name</div>
        <div style={{ width: "120px", textAlign: "center" }}>Actions</div>
      </div>

      <div className="atm-records-area">
        {loading && entriesList.length === 0 ? (
          <div className="text-center p-2">
            {" "}
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : (
          entriesList.map((entry, index) => (
            <div
              key={entry.identifier}
              className="atm-record-item"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "14px 24px",
                backgroundColor: "white",
                borderRadius: "10px",
                marginBottom: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ width: "80px", textAlign: "center" }}>
                {index + 1}
              </div>
              <div style={{ flex: 1, textAlign: "center" }}>
                {entry.accountTypeName}
              </div>
              <div
                style={{
                  width: "120px",
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <button
                  onClick={() => handleInitiateEdit(entry)}
                  style={{ background: "none", border: "none" }}
                >
                  <FaEdit color="#53b7e8" />
                </button>
                <button
                  onClick={() => handlePerformDelete(entry.identifier)}
                  style={{ background: "none", border: "none" }}
                >
                  <FaTrashAlt color="red" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal show={dialogOpen} onHide={() => setDialogOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEntry ? "Edit Account Type" : "Create Account Type"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Account Type Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter account type name"
                value={nameFieldValue}
                onChange={(e) => setNameFieldValue(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
